import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GroceryItemData } from "@/components/GroceryItem";

interface UseGroceryListResult {
  isGenerating: boolean;
  isLoadingSwap: boolean;
  generateList: (goals: string[], userId?: string, customGoal?: string, dietaryRestrictions?: string[]) => Promise<GroceryItemData[]>;
  getAlternatives: (goal: string, excludeNames: string[], currentItemName?: string, dietaryRestrictions?: string[]) => Promise<GroceryItemData[]>;
}

export function useGroceryList(): UseGroceryListResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingSwap, setIsLoadingSwap] = useState(false);

  const generateList = useCallback(async (goals: string[], userId?: string, customGoal?: string, dietaryRestrictions?: string[]): Promise<GroceryItemData[]> => {
    setIsGenerating(true);
    try {
      // Handle custom goal path
      if (customGoal && customGoal.trim()) {
        const { data, error } = await supabase.functions.invoke("generate-grocery-list", {
          body: { customGoal: customGoal.trim(), userId, dietaryRestrictions },
        });

        if (error) throw error;

        if (data.error) {
          throw new Error(data.error);
        }

        return data.items || [];
      }

      // Standard goals path
      const validGoals = ["gut", "brain", "heart", "energy", "immunity", "sleep", "bones", "skin"];
      const sanitizedGoals = goals
        .filter(g => validGoals.includes(g))
        .slice(0, 3);
      
      if (sanitizedGoals.length === 0) {
        throw new Error("No valid goals provided");
      }

      const { data, error } = await supabase.functions.invoke("generate-grocery-list", {
        body: { goals: sanitizedGoals, userId, dietaryRestrictions },
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      return data.items || [];
    } catch (err) {
      console.error("Error generating list:", err);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const getAlternatives = useCallback(async (goal: string, excludeNames: string[], currentItemName?: string, dietaryRestrictions?: string[]): Promise<GroceryItemData[]> => {
    setIsLoadingSwap(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-swap-alternatives", {
        body: { goal, excludeNames, currentItemName, dietaryRestrictions },
      });

      if (error) throw error;

      return data.alternatives || [];
    } catch (err) {
      console.error("Error getting alternatives:", err);
      return [];
    } finally {
      setIsLoadingSwap(false);
    }
  }, []);

  return {
    isGenerating,
    isLoadingSwap,
    generateList,
    getAlternatives,
  };
}
